import Moment from 'moment';
import store from 'store/store';
import { ESTATUS_AUTORIZADO } from './constants';

Moment.locale('es');

export const DATE_FORMAT = 'YYYY-MM-DD';
export const DATE_FORMATS = ['YYYY-MM-DD', 'DD-MM-YYYY'];
export const TIME_FORMAT = 'hh:mm:ss';
export const DATE_FRONTEND_FORMAT = 'DD/MM/YYYY';

export const DECIMAL_PATTERN = /^(\d)*(\.\d{1,2})?$/;
export const NEGATIVE_DECIMAL_PATTERN = /^-(\d)*(\.\d{1,2})?$/;
export const INTEGER_PATTERN = /^\d+$/;
export const INTEGER_PATTERN_REPLACE = /[^0-9]+/g;
export const NEGATIVE_INTEGER_PATTERN_REPLACE = /-[^0-9]+/g;

export const noSpaces = (val) => val?.replace(/\s/g, '');
export const noSymbolsCapitalized = (val) => val?.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
export const toUpper = (val) => val?.toString()?.toUpperCase();
export const toIntegerParsed = (val) => parseInt(`${val}`, 10) || null;
export const toSlug = (val) => val.replace(/ /g, '-').replace(/[^\w-]+/g, '') || null;
export const normalizeText = (text = '') => text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const numberCount = (str = '') => {
  const match = str.match(/([0-9])\1*/g) || [];
  return Object.fromEntries(match.map((itm) => [parseInt(itm.charAt(0), 10), itm.length]));
};
const parse = (val) => parseInt(`0${val}`, 10) || null;

export const integerNormalizer = (val, allowZero = false) => {
  if (val) {
    const numbersCount = numberCount(val);
    const parsedVal = parse(val);
    if (allowZero && numbersCount['0']) {
      return `${'0'.repeat(numbersCount['0'])}${parsedVal || ''}` || null;
    }
    return parsedVal;
  }
  return null;
};

// Regex to keep only digits and hyppen
const replaceNonDigitAndHyphen = /[^\d-]/g;
// Regex to keep only digits and spaces
const replaceNonDigitAndSpace = /[^\d ]/g;

export const customIntegerNormalizer = (_val, allowZero) => {
  const allowedChars = ['-', ' '];
  let val = _val?.toString() || '';
  const char = [...val].find((c) => allowedChars.includes(c));
  val = val.replace(char === '-' ? replaceNonDigitAndHyphen : replaceNonDigitAndSpace);
  // Replace multiple spaces and hyppen
  val = val.replace(/-+/g, '-').replace(/ +/g, ' ');
  if (char) {
    const split = val.split(char);
    const normalized = split.map((e) => integerNormalizer(e, allowZero));
    return normalized
      .reduce((accum, curr, idx) => `${accum}${curr || ''}${idx < normalized.length - 1 ? char : ''}`, '');
  }
  return integerNormalizer(_val, true);
};

export const toInteger = (val = '') => val.replace(/[^0-9]+/g, '') || null;
export const toUpperCase = (val = '') => val.toUpperCase();

/**
 * Format to Upper
 * @param {string|Date} date
 * @param {string[]} formats
 * @returns {Moment}
 */
export const toMoment = (date, formats) => {
  if (date) {
    const moment = new Moment(date, formats);
    if (moment.isValid()) {
      return moment;
    }
  }
  return null;
};

/**
 * Format to Integer
 * @param {string} val
 * @returns number|null
 */
export const decimalNormalizer = (_val, _default = null, allowNegative = false) => {
  if (!_val && !_default) {
    return null;
  }
  const val = _val || _default;
  if ((allowNegative ? NEGATIVE_DECIMAL_PATTERN : DECIMAL_PATTERN).test(val)) {
    if (val === '-') return val;
    return parseFloat(val);
  }
  const value = val.toString();
  const finishWithDot = value.at(-1) === '.';
  const valWithoutDot = finishWithDot ? value.substring(0, value.length - 1) : value;
  const otherDotIdx = valWithoutDot.indexOf('.');
  const hasTwoNonConsecutiveDots = otherDotIdx !== -1
    && (finishWithDot && otherDotIdx !== valWithoutDot.length - 1);
  if ((!finishWithDot || hasTwoNonConsecutiveDots) && parseFloat(valWithoutDot)) {
    return parseFloat(valWithoutDot);
  }
  const preDotValue = toInteger(valWithoutDot, allowNegative) || '';
  return preDotValue.concat(finishWithDot ? '.' : '');
};

export const capitalize = (sentence, fromConfig = true) => {
  const { solo_mayusculas = false } = store.getState()?.auth?.entidad || {};
  if ((fromConfig || solo_mayusculas) && typeof sentence === 'string') return sentence.toUpperCase();
  return sentence;
};

export const titleize = (sentense = '') => sentense.substring(0, 1).toUpperCase().concat(sentense.substring(1));

export const getDistinctValues = (values = []) => [...new Set(values)];

/**
 * List Formatter
 * @param {string[]} list
 * @param {'conjunction'|'disjunction'} type
 * @returns
 */
export const listToString = (
  list,
  type = 'conjunction',
) => new Intl.ListFormat('es-MX', { type }).format(list);

export const normalizeData = (_data) => {
  if (_data) {
    const output = _data.map((item) => {
      const clone = { ...item };
      const unidad_responsable = normalizeData(clone.unidad_responsable);
      const centro_de_costo = normalizeData(clone.centro_de_costo);
      if (!unidad_responsable.length && !centro_de_costo.length) {
        delete clone.unidad_responsable;
        delete clone.centro_de_costo;
        return ({
          id: clone.id,
          title: `${clone.clave} - ${clone.descripcion}`,
          value: `${clone.clave}/${clone.nivel}`,
          tableTitle: clone.descripcion,
          clave: clone.clave,
        });
      }
      if (unidad_responsable) {
        return ({
          id: clone.id,
          title: `${clone.clave} - ${clone.descripcion}`,
          value: `${clone.clave}/${clone.nivel}`,
          children: unidad_responsable,
          tableTitle: clone.descripcion,
          clave: clone.clave,
        });
      }
      return ({
        id: clone.id,
        title: `${clone.clave} - ${clone.descripcion}`,
        value: `${clone.clave}/${clone.nivel}`,
        children: centro_de_costo,
        tableTitle: clone.descripcion,
        clave: clone.clave,
      });
    });
    return output;
  }
  return false;
};

export const areaBuscar = (value, areas) => {
  const stack = [...areas];
  while (stack.length > 0) {
    const areaNueva = stack.pop();
    if (areaNueva.clave === value?.toString() || areaNueva.value === value?.toString()) {
      return areaNueva;
    }
    if (areaNueva.children) {
      stack.push(...areaNueva.children);
    }
  }
  return null;
};

export const translatePermissions = (arregloPermisos) => arregloPermisos.map((permiso) => {
  if (permiso?.name) {
    // eslint-disable-next-line no-param-reassign
    permiso.name = permiso.name
      .replace(/Can add/g, 'Puede Agregar')
      .replace(/Can delete/g, 'Puede Eliminar')
      .replace(/Can view/g, 'Puede Visualizar')
      .replace(/Can change/g, 'Puede Cambiar');
  }
  return permiso;
});

export const filterRoles = (arregloGrupos, arregloRoles) => {
  // eslint-disable-next-line max-len
  const _filteredRoles = arregloGrupos.filter((grupo) => arregloRoles.some((rol) => grupo.name === rol.name && rol.estados_globales === ESTATUS_AUTORIZADO));
  return _filteredRoles;
};
